import { CreditCard, Smartphone } from 'lucide-react'
import type { PortalGateway } from '../hooks/usePaymentGateways'
import { currencyOptionsFor } from '../currencyOptions'

interface GatewayPickerProps {
  gateways: PortalGateway[]
  loading?: boolean
  selectedGatewayId: number | null
  selectedCurrencyId: number | null
  /** La moneda del catálogo, para pasarelas sin restricción de monedas. */
  fallbackCurrency?: { id: number; name: string } | null
  onSelect: (gatewayId: number, currencyId: number | null) => void
  onSelectCurrency: (currencyId: number) => void
}

function iconFor(code: string) {
  return code.toLowerCase().includes('transfermovil') ? (
    <Smartphone className="size-5" />
  ) : (
    <CreditCard className="size-5" />
  )
}

/**
 * Selector de pasarela de pago del autoservicio (pagos-zeus-3). Las pasarelas
 * son dinámicas: externas de Zeus habilitadas por el admin del portal. Al
 * elegir pasarela se auto-selecciona su primera moneda elegible.
 */
export function GatewayPicker({
  gateways,
  loading,
  selectedGatewayId,
  selectedCurrencyId,
  fallbackCurrency,
  onSelect,
  onSelectCurrency,
}: GatewayPickerProps) {
  if (loading) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Cargando métodos de pago…
      </p>
    )
  }
  if (gateways.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        No hay métodos de pago disponibles en este momento. Podrás pagar tu
        factura más adelante desde la sección de facturación.
      </p>
    )
  }

  const selected = gateways.find((g) => g.id === selectedGatewayId)
  const currencies = currencyOptionsFor(selected, fallbackCurrency)

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        {gateways.map((g) => {
          const active = g.id === selectedGatewayId
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => {
                const options = currencyOptionsFor(g, fallbackCurrency)
                onSelect(g.id, options[0]?.id ?? null)
              }}
              aria-pressed={active}
              className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${
                active
                  ? 'border-primary bg-primary/5'
                  : 'border-black/10 hover:border-black/25'
              }`}
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-neutral-100 text-foreground">
                {iconFor(g.code)}
              </span>
              <span>
                <span className="block text-sm font-semibold">{g.name}</span>
                <span className="block text-[11px] text-muted-foreground">
                  Pago en línea seguro
                </span>
              </span>
            </button>
          )
        })}
      </div>

      {selected && currencies.length > 1 && (
        <div>
          <span className="text-xs font-semibold text-muted-foreground">
            Moneda del pago
          </span>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {currencies.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onSelectCurrency(c.id)}
                aria-pressed={c.id === selectedCurrencyId}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                  c.id === selectedCurrencyId
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-black/10 hover:border-black/25'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
